import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import {
  bindVerifyPromptButtons,
  isEmailNotVerifiedError,
  verifyEmailPromptHtml,
} from '../components/VerifyEmailPrompt.js';
import { attachNameSuggest, fetchPlayerSuggestions } from '../components/NameSuggest.js';
import type {
  MailDetailDto,
  MailFolder,
  MailSummaryDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn' | 'verify';
  message: string;
}

interface State {
  folder: MailFolder;
  mails: MailSummaryDto[];
  unreadCount: number;
  total: number;
  selected: MailDetailDto | null;
  composing: boolean;
  initialTo: string;
  flash: FlashState | null;
}

export async function renderMail(root: HTMLElement): Promise<void> {
  // If the route is #/mail/:id, preselect that mail.
  const hash = window.location.hash;
  const idMatch = hash.match(/^#\/mail\/([^/?]+)/);
  const idMatchGroup = idMatch?.[1];
  const preselectId = idMatchGroup ? decodeURIComponent(idMatchGroup) : null;

  // Compose deep-link: #/mail?to=Name (e.g. the Send Mail button on a profile).
  const toMatch = hash.match(/[?&]to=([^&]+)/);
  const preToHandle = toMatch?.[1] ? decodeURIComponent(toMatch[1]) : '';

  const state: State = {
    folder: 'inbox',
    mails: [],
    unreadCount: 0,
    total: 0,
    selected: null,
    composing: !!preToHandle,
    initialTo: preToHandle,
    flash: null,
  };

  async function refreshList(): Promise<void> {
    try {
      const r = await api.listMail({ folder: state.folder });
      state.mails = r.mails;
      state.unreadCount = r.unreadCount;
      state.total = r.total;
    } catch (err) {
      state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load mail.' };
    }
  }

  async function openMail(id: string): Promise<void> {
    try {
      const r = await api.getMail(id);
      state.selected = r.mail;
      // Refresh the list so its read state updates.
      await refreshList();
    } catch (err) {
      state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to open mail.' };
    }
  }

  await refreshList();
  if (preselectId) await openMail(preselectId);

  mountShell(root, (main) => {
    const renderMain = (): void => {
      main.innerHTML = `
        <img class="page-banner" src="/img/banners/mail.webp" alt="" />
        <h1>The Post</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Letters carried by horse, hand, and hawk.
        </p>

        <div id="flash"></div>

        <div class="mail-layout">
          <aside class="mail-sidebar">
            <div class="mail-tabs">
              <button class="mail-tab ${state.folder === 'inbox' ? 'active' : ''}" data-folder="inbox">
                Inbox ${state.unreadCount > 0 ? `<span class="mail-unread-pip">${state.unreadCount}</span>` : ''}
              </button>
              <button class="mail-tab ${state.folder === 'sent' ? 'active' : ''}" data-folder="sent">Sent</button>
            </div>
            <button class="btn mail-compose-btn" id="compose-btn">✍ Compose</button>
            <div class="mail-list" id="mail-list">
              ${
                state.mails.length === 0
                  ? `<div class="mail-empty">No ${state.folder === 'inbox' ? 'mail in your inbox' : 'mail sent'} yet.</div>`
                  : state.mails.map(renderMailRow).join('')
              }
            </div>
          </aside>

          <section class="mail-pane ${state.composing || state.selected ? 'mail-pane--active' : ''}">
            ${
              state.composing || state.selected
                ? '<button class="btn ghost mail-back-btn" id="mail-back">← Back to letters</button>'
                : ''
            }
            ${
              state.composing
                ? renderComposer(state.initialTo)
                : state.selected
                  ? renderReader(state.selected, state.folder)
                  : `<div class="mail-empty mail-pane-empty">
                       <p>Select a letter from your inbox, or compose a new one.</p>
                     </div>`
            }
          </section>
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = MAIL_STYLES;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (state.flash) {
        if (state.flash.kind === 'verify') {
          flashRoot.innerHTML = verifyEmailPromptHtml(state.flash.message);
          bindVerifyPromptButtons(flashRoot);
        } else {
          const div = document.createElement('div');
          div.className = `alert ${state.flash.kind === 'warn' ? 'danger' : state.flash.kind}`;
          div.textContent = state.flash.message;
          flashRoot.appendChild(div);
        }
      }

      wireListeners();
    };

    function wireListeners(): void {
      // Tab switch.
      main.querySelectorAll<HTMLButtonElement>('button.mail-tab').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const folder = btn.dataset.folder as MailFolder;
          if (folder === state.folder) return;
          state.folder = folder;
          state.selected = null;
          state.composing = false;
          await refreshList();
          anchored(renderMain);
        });
      });

      // Compose.
      main.querySelector<HTMLButtonElement>('#compose-btn')?.addEventListener('click', () => {
        state.composing = true;
        state.selected = null;
        state.initialTo = '';
        anchored(renderMain);
        main.querySelector<HTMLInputElement>('#compose-to')?.focus();
      });

      // Recipient typeahead — same suggest mechanic as the Attacks page.
      const composeTo = main.querySelector<HTMLInputElement>('#compose-to');
      if (composeTo) attachNameSuggest(composeTo, fetchPlayerSuggestions);

      // Open a mail.
      main.querySelectorAll<HTMLElement>('.mail-row').forEach((row) => {
        row.addEventListener('click', async () => {
          const id = row.dataset.id!;
          await openMail(id);
          state.composing = false;
          anchored(renderMain);
        });
      });

      // Back button (mobile pop-out) — close the reader/composer overlay.
      main.querySelector<HTMLButtonElement>('#mail-back')?.addEventListener('click', () => {
        state.selected = null;
        state.composing = false;
        anchored(renderMain);
      });

      // Reader actions.
      main.querySelector<HTMLButtonElement>('#reply-btn')?.addEventListener('click', () => {
        if (!state.selected) return;
        state.composing = true;
        state.initialTo = state.selected.otherName;
        state.selected = null;
        anchored(renderMain);
        const subjEl = main.querySelector<HTMLInputElement>('#compose-subject');
        if (subjEl && !subjEl.value && state.initialTo) {
          // pre-fill subject for replies — but we wiped selected, so just leave it blank
        }
        main.querySelector<HTMLInputElement>('#compose-subject')?.focus();
      });
      main.querySelector<HTMLButtonElement>('#delete-btn')?.addEventListener('click', async () => {
        if (!state.selected) return;
        const id = state.selected.id;
        try {
          await api.deleteMail(id);
          state.flash = { kind: 'success', message: 'Mail deleted.' };
          state.selected = null;
          await refreshList();
        } catch (err) {
          state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Delete failed.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Composer.
      main.querySelector<HTMLButtonElement>('#compose-cancel')?.addEventListener('click', () => {
        state.composing = false;
        anchored(renderMain);
      });
      main.querySelector<HTMLButtonElement>('#compose-send')?.addEventListener('click', async () => {
        const toInput = main.querySelector<HTMLInputElement>('#compose-to')!;
        const subjInput = main.querySelector<HTMLInputElement>('#compose-subject')!;
        const bodyInput = main.querySelector<HTMLTextAreaElement>('#compose-body')!;
        const toHandle = toInput.value.trim();
        const subject = subjInput.value.trim();
        const body = bodyInput.value.trim();
        if (!toHandle || !subject || !body) {
          state.flash = { kind: 'warn', message: 'Recipient, subject, and message are all required.' };
          anchored(renderMain);
          return;
        }
        const btn = main.querySelector<HTMLButtonElement>('#compose-send')!;
        btn.disabled = true;
        try {
          const r = await api.sendMail({ toHandle, subject, body });
          state.flash = { kind: 'success', message: `Sent to ${r.recipientName}.` };
          state.composing = false;
          if (state.folder === 'sent') await refreshList();
        } catch (err) {
          if (isEmailNotVerifiedError(err)) {
            state.flash = { kind: 'verify', message: err.message };
          } else {
            state.flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Send failed.' };
          }
        } finally {
          anchored(renderMain);
        }
      });
    }

    anchored(renderMain);
  });
}

function renderMailRow(m: MailSummaryDto): string {
  const isUnread = !m.readAt && !m.fromMe;
  const when = formatRelative(m.sentAt);
  return `
    <div class="mail-row ${isUnread ? 'unread' : ''}" data-id="${m.id}">
      <div class="mail-row-meta">
        <span class="mail-row-name">${escapeHtml(m.otherName)}</span>
        <span class="mail-row-time">${when}</span>
      </div>
      <div class="mail-row-subject">${escapeHtml(m.subject)}</div>
      <div class="mail-row-preview">${escapeHtml(m.preview)}</div>
    </div>
  `;
}

function renderReader(m: MailDetailDto, folder: MailFolder): string {
  return `
    <div class="mail-reader">
      <div class="mail-reader-head">
        <h2>${escapeHtml(m.subject)}</h2>
        <div class="mail-reader-meta">
          ${m.fromMe ? 'To' : 'From'} <strong>${
            m.otherId
              ? `<a href="#/u/${encodeURIComponent(m.otherId)}">${escapeHtml(m.otherName)}</a>`
              : escapeHtml(m.otherName)
          }</strong>
          · ${new Date(m.sentAt).toLocaleString()}
        </div>
      </div>
      <div class="mail-reader-body">${escapeHtml(m.body).replace(/\n/g, '<br/>')}</div>
      <div class="mail-reader-actions">
        ${folder === 'inbox' && m.otherId ? '<button class="btn" id="reply-btn">Reply</button>' : ''}
        ${folder === 'inbox' ? '<button class="btn ghost" id="delete-btn">Delete</button>' : ''}
      </div>
    </div>
  `;
}

function renderComposer(initialTo: string): string {
  return `
    <div class="mail-composer">
      <h2 style="margin-top: 0;">New Letter</h2>
      <div class="field">
        <label for="compose-to">To (player handle)</label>
        <input id="compose-to" type="text" maxlength="64" value="${escapeHtml(initialTo)}" />
      </div>
      <div class="field">
        <label for="compose-subject">Subject</label>
        <input id="compose-subject" type="text" maxlength="200" />
      </div>
      <div class="field">
        <label for="compose-body">Message</label>
        <textarea id="compose-body" rows="10" maxlength="4000" style="resize: vertical;"></textarea>
      </div>
      <div style="display: flex; gap: var(--s-2);">
        <button class="btn" id="compose-send">Send</button>
        <button class="btn ghost" id="compose-cancel">Cancel</button>
      </div>
    </div>
  `;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const MAIL_STYLES = `
  .mail-layout {
    display: grid;
    /* minmax(0,…) (not the default minmax(auto,1fr)) lets each track shrink
       below its content's intrinsic width — without it the nowrap subject/
       preview text forces the sidebar (and the whole page) wider than the
       viewport on mobile, pushing the tab + Compose labels off-screen. */
    grid-template-columns: 320px minmax(0, 1fr);
    gap: var(--s-4);
    margin-top: var(--s-3);
    align-items: start;
  }
  @media (max-width: 800px) {
    .mail-layout { grid-template-columns: minmax(0, 1fr); }
  }
  .mail-sidebar {
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius);
    padding: var(--s-3);
    box-shadow: var(--shadow-soft);
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
    min-width: 0; /* allow the grid item to clamp instead of overflowing */
  }
  .mail-tabs {
    display: flex;
    gap: var(--s-2);
  }
  .mail-tab {
    flex: 1;
    font-family: var(--font-display);
    font-size: 0.95rem;
    letter-spacing: 0.03em;
    padding: 6px 10px;
    background: rgba(255, 245, 220, 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    /* The cell stays light-cream in both themes, so pin dark ink (the soft-ink
       token flips to cream in dark mode and washed out on this cell). */
    color: rgba(28, 17, 8, 0.75);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .mail-tab.active {
    background: var(--c-oxblood);
    color: #fff5e1;
    border-color: var(--c-oxblood-deep);
  }
  .mail-unread-pip {
    display: inline-block;
    min-width: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--c-gold);
    color: var(--c-oxblood-deep);
    font-family: var(--font-numeric);
    font-weight: 800;
    font-size: 0.78rem;
    text-align: center;
  }
  .mail-compose-btn { width: 100%; margin-bottom: var(--s-2); }
  .mail-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 520px;
    overflow-y: auto;
    min-width: 0; /* clamp so nowrap rows ellipsis instead of stretching */
  }
  .mail-empty {
    font-family: var(--font-elegant);
    font-style: italic;
    color: var(--c-ink-soft);
    padding: var(--s-3);
    text-align: center;
  }
  .mail-pane-empty { padding: var(--s-6); }
  .mail-row {
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: var(--radius);
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
    min-width: 0; /* let the nowrap subject/preview children clip to width */
  }
  .mail-row:hover {
    background: rgba(212, 169, 60, 0.14);
    border-color: var(--c-gold);
  }
  .mail-row.unread {
    background: rgba(107, 31, 31, 0.05);
    border-color: var(--c-oxblood);
  }
  .mail-row.unread .mail-row-subject { font-weight: 700; color: var(--c-oxblood); }
  .mail-row-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-family: var(--font-display);
    font-size: 0.82rem;
    color: var(--c-ink-soft);
    margin-bottom: 2px;
  }
  .mail-row-name {
    font-weight: 700;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mail-row-time { flex: none; }
  .mail-row-subject {
    font-family: var(--font-body);
    font-size: 0.95rem;
    color: var(--c-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mail-row-preview {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 0.92rem;
    color: var(--c-ink-soft);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mail-pane {
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule-strong);
    border-radius: var(--radius);
    box-shadow: var(--shadow-soft);
    padding: var(--s-5);
    min-height: 420px;
  }
  /* Back button only appears in the mobile pop-out (hidden on desktop, where the
     reader sits inline beside the list). */
  .mail-back-btn { display: none; }
  /* On mobile the layout is a single column, so an opened letter rendered inline
     lands far below the list and is awkward to scroll to. Instead, pop the
     reader/composer over the whole screen so it's front-and-centre on open. */
  @media (max-width: 800px) {
    .mail-pane--active {
      position: fixed;
      inset: 0;
      z-index: 200;
      margin: 0;
      border-radius: 0;
      min-height: 100vh;
      max-height: 100vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    .mail-pane--active .mail-back-btn {
      display: inline-flex;
      margin-bottom: var(--s-3);
    }
  }
  .mail-reader h2 { margin: 0 0 4px 0; }
  .mail-reader-meta {
    font-family: var(--font-display);
    font-size: 0.9rem;
    color: var(--c-ink-soft);
    margin-bottom: var(--s-4);
    padding-bottom: var(--s-3);
    border-bottom: 1px solid var(--c-rule);
  }
  .mail-reader-body {
    font-family: var(--font-body);
    font-size: 1.05rem;
    line-height: 1.55;
    color: var(--c-ink);
    margin-bottom: var(--s-5);
    overflow-wrap: anywhere; /* long unbroken strings (URLs) wrap, not overflow */
  }
  .mail-reader-actions {
    display: flex;
    gap: var(--s-2);
  }
  .mail-composer .field input,
  .mail-composer .field textarea {
    font: inherit;
    font-family: var(--font-body);
    padding: var(--s-2) var(--s-3);
    /* Colours come from the global pinned cream cell in styles/pages.css. */
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    box-shadow: var(--shadow-press);
    width: 100%;
  }
  .mail-composer .field input:focus,
  .mail-composer .field textarea:focus {
    outline: none;
    border-color: var(--c-gold);
    box-shadow: var(--shadow-press), 0 0 0 3px rgba(212, 169, 60, 0.25);
  }
`;
